/**
 * Feature-detected shims for browser APIs outside the required set (ADR-0050 decision 13). A lint
 * rule bans direct use of `scheduler.yield`, `requestIdleCallback` and `DecompressionStream`
 * anywhere else, so Safari and Firefox always get the fallback path.
 */

type Scheduler = { yield?: () => Promise<void> };
type IdleDeadline = { didTimeout: boolean; timeRemaining(): number };
type Timer = unknown;
interface PlatformGlobals {
  scheduler?: Scheduler;
  requestIdleCallback?: (cb: (d: IdleDeadline) => void, opts?: { timeout?: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
  MessageChannel?: new () => {
    port1: { onmessage: ((ev: unknown) => void) | null; close?(): void };
    port2: { postMessage(msg: unknown): void; close?(): void };
  };
  setTimeout: (cb: () => void, ms?: number) => Timer;
  clearTimeout: (t: Timer) => void;
  DecompressionStream?: new (format: string) => unknown;
  Response: new (body: unknown) => { body: { pipeThrough(t: unknown): unknown } | null; arrayBuffer(): Promise<ArrayBuffer> };
  Blob: new (parts: unknown[]) => { stream(): { pipeThrough(t: unknown): unknown } };
}

const g = globalThis as unknown as PlatformGlobals;

/** Yield to the event loop so input and paint can run (scheduler.yield, else a MessageChannel post). */
export function yieldToMain(): Promise<void> {
  const s = g.scheduler;
  if (s && typeof s.yield === 'function') return s.yield.call(s);
  const MC = g.MessageChannel;
  if (MC) {
    return new Promise<void>((resolve) => {
      const ch = new MC();
      ch.port1.onmessage = () => {
        ch.port1.close?.();
        ch.port2.close?.();
        resolve();
      };
      ch.port2.postMessage(null);
    });
  }
  return new Promise<void>((resolve) => {
    g.setTimeout(resolve, 0);
  });
}

export interface IdleHandle {
  cancel(): void;
}

/** Run `fn` when the browser is idle (requestIdleCallback, else setTimeout 200 ms). */
export function onIdle(fn: () => void, opts: { timeout?: number } = {}): IdleHandle {
  if (typeof g.requestIdleCallback === 'function') {
    const h = g.requestIdleCallback(() => fn(), opts.timeout === undefined ? undefined : { timeout: opts.timeout });
    return { cancel: () => g.cancelIdleCallback?.(h) };
  }
  const t = g.setTimeout(fn, 200);
  return { cancel: () => g.clearTimeout(t) };
}

export type CompressionFormat = 'br' | 'gzip';

function canDecompress(format: CompressionFormat): boolean {
  const DS = g.DecompressionStream;
  if (!DS) return false;
  try {
    new DS(format);
    return true;
  } catch {
    return false;
  }
}

/** Formats this runtime can decompress, best first (sent as `hello.zAccept`). */
export function supportedDecompression(): CompressionFormat[] {
  return (['br', 'gzip'] as const).filter(canDecompress);
}

/** Decompress `bytes` in `format`; rejects when the runtime can't (callers negotiate first). */
export async function decompress(bytes: Uint8Array, format: CompressionFormat): Promise<Uint8Array> {
  const DS = g.DecompressionStream;
  if (!DS || !canDecompress(format)) throw new Error(`decompression format not supported: ${format}`);
  const stream = new g.Blob([bytes]).stream().pipeThrough(new DS(format));
  const buf = await new g.Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}
